import React, { useCallback, useMemo, useRef, useState } from 'react';
import { type CellContext, type ColumnDef } from '@tanstack/react-table';
import { Alert, InlineEditRowActions, Table, TextLink, Tooltip } from '@shared/components';
import { useInlineRowEdit } from '@shared/hooks/useInlineRowEdit';
import { useLatestRef } from '@shared/hooks/useLatestRef';
import { PermissionGuard } from '@/features/auth';
import { SquarePenIcon, Trash2Icon } from 'lucide-react';
import type { ConfigSetting } from '@/features/products/types/products.types';
import { UpdateAddOnAssignmentSchema } from '../../schemas/productAddon.schema';
import { summariseAutoInclude } from '../../utils/autoIncludeLinkOptions';
import type {
    ProductAddOnRecord,
    UpdateAddOnAssignmentPayload,
} from '../../services/productAddons';
import {
    HelpHeader,
    DraftInput,
    DraftPeriodSelect,
    DraftPriceOverrideWithGross,
    AutoIncludeEditCell,
} from './DraftControls';

// Live values ride on table meta, not columns deps: recomputing `columns`
// remounts the open editor (see the `Table` meta prop).
interface TableMeta {
    isPending?: boolean;
    configSettings?: ConfigSetting[] | null;
}

const tableMeta = (info: CellContext<ProductAddOnRecord, unknown>): TableMeta =>
    (info.table.options.meta as TableMeta | undefined) ?? {};

const isSavePending = (info: CellContext<ProductAddOnRecord, unknown>): boolean => !!tableMeta(info).isPending;

interface Props {
    data: ProductAddOnRecord[];
    configSettings?: ConfigSetting[] | null;
    onSave: (addonId: string, payload: UpdateAddOnAssignmentPayload) => void | Promise<unknown>;
    onDetach: (addonId: string) => void;
}

export const ProductAddOnDataTable: React.FC<Props> = ({ data, configSettings, onSave, onDetach }) => {
    const inlineEdit = useInlineRowEdit<string>();
    const [editError, setEditError] = useState<string | null>(null);
    // Per-keystroke drafts, mirrored from the Draft* widgets — kept out of column
    // memo deps so typing never rebuilds cell identities.
    const priceOverrideRef = useRef('');
    const freqLimitRef = useRef('');
    const freqPeriodRef = useRef('');
    const qtyLimitRef = useRef('');
    const autoIncludeRef = useRef(false);
    const mandatoryRef = useRef(false);
    const autoIncludeLinkRef = useRef('');
    // Read through refs, not column memo deps: callers recreate these props every
    // render, and rebuilding the cells remounts an open editor.
    const onSaveRef = useLatestRef(onSave);
    const onDetachRef = useLatestRef(onDetach);

    const startEdit = useCallback((row: ProductAddOnRecord) => {
        setEditError(null);
        priceOverrideRef.current = row.pivot.price_override ?? '';
        freqLimitRef.current = row.pivot.frequency_limit != null ? String(row.pivot.frequency_limit) : '';
        freqPeriodRef.current = row.pivot.frequency_period ?? '';
        qtyLimitRef.current = row.pivot.quantity_limit != null ? String(row.pivot.quantity_limit) : '';
        autoIncludeRef.current = row.pivot.auto_include;
        mandatoryRef.current = row.pivot.mandatory;
        autoIncludeLinkRef.current = row.pivot.auto_include_link ?? '';
        inlineEdit.beginEdit(row.id);
        // `beginEdit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.beginEdit]);

    const cancelEdit = useCallback(() => {
        inlineEdit.cancelEdit();
        setEditError(null);
        // `cancelEdit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.cancelEdit]);

    const saveEdit = useCallback(() => {
        const id = inlineEdit.editingId;
        if (!id) return;
        const parsed = UpdateAddOnAssignmentSchema.safeParse({
            price_override: priceOverrideRef.current,
            frequency_limit: freqLimitRef.current,
            frequency_period: freqPeriodRef.current,
            quantity_limit: qtyLimitRef.current,
            auto_include: autoIncludeRef.current,
            mandatory: mandatoryRef.current,
            auto_include_link: autoIncludeLinkRef.current,
        });
        if (!parsed.success) {
            setEditError(parsed.error.issues[0]?.message ?? 'Invalid override values.');
            return;
        }
        setEditError(null);
        // Close only once the update resolves, so a failed save keeps the draft in
        // place to retry (the panel surfaces the API error).
        inlineEdit
            .commit(() => Promise.resolve(onSaveRef.current(id, parsed.data as UpdateAddOnAssignmentPayload)).then(() => undefined))
            .catch(() => { /* keep the row open with the draft intact */ });
        // `commit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.editingId, inlineEdit.commit, onSaveRef]);

    const columns = useMemo<ColumnDef<ProductAddOnRecord>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'category',
            header: 'Category',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? '—'}</span>,
        },
        {
            accessorKey: 'price',
            header: () => (
                <HelpHeader
                    label="Default Net"
                    help="The optional extra's catalogue net price (excludes VAT). Read-only."
                />
            ),
            cell: (info) => <span className="text-sm font-mono">£{info.getValue() as string}</span>,
        },
        {
            id: 'price_override',
            header: () => (
                <HelpHeader
                    label="Net Override"
                    help="Optional net price for this optional extra on this product only. Blank uses the default net price."
                />
            ),
            cell: (info) => {
                const row = info.row.original;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <DraftPriceOverrideWithGross
                            draftRef={priceOverrideRef}
                            vatRatePercent={row.vat_rate_percent}
                            disabled={isSavePending(info)}
                            autoFocus
                        />
                    );
                }
                const override = row.pivot.price_override;
                return override
                    ? <span className="text-sm font-mono italic">£{override}</span>
                    : <span className="text-sm text-mp-muted">—</span>;
            },
        },
        {
            id: 'effective_price',
            header: () => (
                <HelpHeader
                    label="Effective Net"
                    help="The net price actually charged: the override if set, otherwise the default net price."
                />
            ),
            cell: (info) => (
                <span className="text-sm font-mono font-semibold text-mp-success">
                    £{info.row.original.effective_price}
                </span>
            ),
        },
        {
            id: 'frequency',
            header: () => (
                <HelpHeader
                    label="Frequency"
                    help="Optional usage cap — how many times the optional extra can be claimed per period (limit / period)."
                />
            ),
            cell: (info) => {
                const row = info.row.original;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <div className="flex items-center gap-1">
                            <DraftInput
                                id={`freq-limit-${row.id}`}
                                label="Frequency limit"
                                draftRef={freqLimitRef}
                                type="number"
                                min="1"
                                className="w-20"
                                placeholder="—"
                                disabled={isSavePending(info)}
                            />
                            <span className="text-xs">/</span>
                            <DraftPeriodSelect draftRef={freqPeriodRef} disabled={isSavePending(info)} />
                        </div>
                    );
                }
                const { frequency_limit, frequency_period } = row.pivot;
                return frequency_limit && frequency_period
                    ? <span className="text-sm">{frequency_limit} / {frequency_period}</span>
                    : <span className="text-sm text-mp-muted">—</span>;
            },
        },
        {
            id: 'quantity_limit',
            header: () => (
                <HelpHeader
                    label="Qty Limit"
                    help="Optional cap on how many of this optional extra can be added to the product."
                />
            ),
            cell: (info) => {
                const row = info.row.original;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <DraftInput
                            id={`qty-limit-${row.id}`}
                            label="Quantity limit"
                            draftRef={qtyLimitRef}
                            type="number"
                            min="1"
                            className="w-20"
                            placeholder="—"
                            disabled={isSavePending(info)}
                        />
                    );
                }
                const q = row.pivot.quantity_limit;
                return q
                    ? <span className="text-sm">{q}</span>
                    : <span className="text-sm text-mp-muted">—</span>;
            },
        },
        {
            id: 'auto_include',
            header: () => (
                <HelpHeader
                    label="Auto-include"
                    help="Seeds automatically into a quote for this product; mandatory lines cannot be removed."
                />
            ),
            cell: (info) => {
                const row = info.row.original;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <AutoIncludeEditCell
                            autoIncludeRef={autoIncludeRef}
                            mandatoryRef={mandatoryRef}
                            linkRef={autoIncludeLinkRef}
                            configSettings={tableMeta(info).configSettings}
                            disabled={isSavePending(info)}
                        />
                    );
                }
                const summary = summariseAutoInclude(row.pivot);
                return summary
                    ? <span className="text-sm">{summary}</span>
                    : <span className="text-sm text-mp-muted">—</span>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const row = info.row.original;

                if (inlineEdit.editingId === row.id) {
                    return (
                        <InlineEditRowActions
                            isPending={isSavePending(info)}
                            onAccept={saveEdit}
                            onCancel={cancelEdit}
                        />
                    );
                }

                return (
                    <div className="flex gap-2">
                        <PermissionGuard permission={{ resource: 'smr', action: 'product-addon.edit' }}>
                            <Tooltip content="Edit overrides">
                                <TextLink
                                    ariaLabel="Edit overrides"
                                    color="secondary"
                                    onClick={() => startEdit(row)}
                                    disabled={!!inlineEdit.editingId}
                                    startIcon={<SquarePenIcon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                        <PermissionGuard permission={{ resource: 'smr', action: 'product-addon.delete' }}>
                            <Tooltip content="Detach optional extra">
                                <TextLink
                                    ariaLabel="Detach optional extra"
                                    color="error"
                                    onClick={() => onDetachRef.current(row.id)}
                                    disabled={!!inlineEdit.editingId}
                                    startIcon={<Trash2Icon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [inlineEdit.editingId, onDetachRef, saveEdit, startEdit, cancelEdit]);

    return (
        <div className="space-y-2">
            {editError && <Alert variant="error">{editError}</Alert>}
            <Table<ProductAddOnRecord>
                data={data}
                columns={columns}
                enableSorting={false}
                meta={{ isPending: inlineEdit.isPending, configSettings }}
            />
        </div>
    );
};
